import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import * as Icons from 'lucide-react'
import { User, Users, MapPin, Trophy, Bell, Coins, Sparkles, Radio, Calendar, Zap, Crosshair } from 'lucide-react'
import { meAPI } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { io as socketIO } from 'socket.io-client'

/* "Voor mij" — persoonlijke tijdlijn bovenaan de home. Toont wat ER NU voor
   deze user telt: zijn compo's, zijn team, zijn seat, zijn recente rewards.
   Vervangt het anonieme home-gevoel door één blok waar de deelnemer zich
   in herkent. */

function icoonVan(naam) { return Icons[naam] || Bell }

export default function VoorMij() {
  const { user } = useAuth()
  const [data, setData] = useState(null)
  const [err, setErr]   = useState(null)

  const laad = async () => {
    try { const r = await meAPI.timeline(); setData(r.data.timeline) }
    catch (e) { setErr(e.response?.data?.error ?? e.message) }
  }
  useEffect(() => { if (user) laad() }, [user?.id])

  // Live-refresh op notification:new — zorgt dat "Voor mij" mee klopt.
  useEffect(() => {
    if (!user) return
    const token = localStorage.getItem('auth_token')
    if (!token) return
    const s = socketIO({ path: '/socket.io', auth: { token }, transports: ['websocket', 'polling'] })
    s.on('notification:new', laad)
    return () => s.disconnect()
  }, [user?.id])

  if (!user) return null
  if (err) return <p className="text-sm text-signal mb-4">{err}</p>
  if (!data) return null

  const naam = user.display_name || user.username

  // Prioriteit-lijst — de "één ding dat je nu moet weten"-kop.
  const live = data.tournaments.find(t => t.status === 'in_progress')
  // Alleen tellen als check-in-feature aanstaat voor dit toernooi. Anders
   // is checked_in_at altijd leeg en zou "Check-in nog te doen" onterecht
   // permanent blijven zichtbaar.
  const wachtOpCheckin = data.tournaments.find(t =>
    t.status === 'registration' && !t.checked_in_at && t.features?.check_in)
  const wachtOpReady = data.tournaments.find(t => t.status === 'in_progress' && !t.ready_at)
  const winnaarNotif = (data.notifications || []).find(n =>
    ['compo_result','prize','badge'].includes(n.type) && !n.read_at)
  const openMapveto = (data.mapvetos || [])[0]

  // Deadline-helper: als check-in-toernooi een starttijd heeft, toon "sluit
  // binnen X min" zodat deelnemer niet vergeet zich in te checken.
  const checkinCountdown = (t) => {
    if (!t?.start_time) return null
    const ms = new Date(t.start_time).getTime() - Date.now()
    if (ms <= 0) return 'gestart — check nu in of je valt uit'
    const min = Math.floor(ms / 60000)
    if (min < 1) return 'sluit binnen 1 min'
    if (min < 60) return `sluit binnen ${min} min`
    const uur = Math.floor(min / 60)
    return `sluit binnen ${uur}u ${min % 60}m`
  }

  const hero =
    winnaarNotif
      ? { icon: Trophy, kleur: winnaarNotif.accent || '#E4C265', label: winnaarNotif.title, sub: winnaarNotif.message, to: winnaarNotif.action_url }
      : openMapveto
        ? {
            icon: Crosshair, kleur: '#FF3399',
            label: 'Map-veto bezig',
            sub: `${openMapveto.team_a_name} vs ${openMapveto.team_b_name} — beurt aan team ${openMapveto.current_turn.toUpperCase()}`,
            to: `/mapveto/${openMapveto.uuid}`,
          }
        : wachtOpReady
          ? { icon: Zap, kleur: '#FF5A5A', label: 'Klaarmelden voor je match', sub: wachtOpReady.name, to: `/tournaments/${wachtOpReady.id}` }
          : live
            ? { icon: Radio, kleur: '#B4FF39', label: 'Je toernooi loopt', sub: live.name, to: `/tournaments/${live.id}` }
            : wachtOpCheckin
              ? {
                  icon: MapPin, kleur: '#FFB57A',
                  label: 'Check-in nog te doen',
                  sub: [wachtOpCheckin.name, checkinCountdown(wachtOpCheckin)].filter(Boolean).join(' · '),
                  to: `/tournaments/${wachtOpCheckin.id}`,
                }
              : null

  return (
    <section className="rounded-md border border-border bg-gradient-to-br from-card via-card to-transparent p-5 mb-6">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-acid" />
          <span className="eyebrow">
            Voor {naam}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Coins className="h-3.5 w-3.5 text-gold" />
            <span className="tabular-nums font-semibold text-foreground">{data.points_balance}</span> pt
          </div>
          <Link to={`/spelers/${user.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-acid/40 bg-acid/10
                           text-acid text-xs font-semibold uppercase tracking-wider
                           hover:bg-acid hover:text-background transition-colors">
            <User className="h-3.5 w-3.5" />
            Mijn profiel
            <Icons.ChevronRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>

      {hero && (
        <Link to={hero.to || '#'}
              className="block rounded-md border border-border bg-card p-4 mb-3 hover:border-acid transition-colors group"
              style={{ borderLeft: `3px solid ${hero.kleur}` }}>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md grid place-items-center shrink-0"
                 style={{ background: `${hero.kleur}22` }}>
              <hero.icon className="h-5 w-5" style={{ color: hero.kleur }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm group-hover:text-acid transition-colors">{hero.label}</div>
              {hero.sub && <div className="text-xs text-muted-foreground truncate">{hero.sub}</div>}
            </div>
            <Icons.ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-acid transition-colors" />
          </div>
        </Link>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Tegel icon={Trophy} kleur="#E4C265" label="Mijn compo's" telling={data.tournaments.length + data.show_entries.length}>
          {data.tournaments.length === 0 && data.show_entries.length === 0
            ? <span className="text-muted-foreground">Niks lopend.</span>
            : (
              <div className="space-y-1">
                {data.tournaments.slice(0, 3).map(t => (
                  <Link key={t.id} to={`/tournaments/${t.id}`}
                        className="flex items-center justify-between text-xs hover:text-acid transition-colors">
                    <span className="truncate">{t.name}</span>
                    <StatusChip status={t.status} />
                  </Link>
                ))}
                {data.show_entries.slice(0, 2).map((s, i) => (
                  <Link key={`s-${i}`} to={s.kind === 'kempenvision' ? '/kempenvision' : '/imagecompo'}
                        className="flex items-center justify-between text-xs hover:text-acid transition-colors">
                    <span className="truncate">{s.show_name} · {s.entrant_name}</span>
                    <span className="font-mono text-[9px] uppercase tracking-widest text-peach">{s.phase}</span>
                  </Link>
                ))}
              </div>
            )}
        </Tegel>

        <Tegel icon={Users} kleur="#B4FF39" label="Mijn teams" telling={data.teams.length}>
          {data.teams.length === 0
            ? <span className="text-muted-foreground">Nog geen team. <Link to="/teams" className="text-acid hover:underline">Aanmaken →</Link></span>
            : (
              <div className="flex flex-wrap gap-1.5">
                {data.teams.map(t => {
                  const Icon = icoonVan(t.icon_name)
                  return (
                    <Link key={t.id} to={`/teams/${t.id}`}
                          className="inline-flex items-center gap-1.5 rounded-[4px] border border-border bg-card px-2 py-1 hover:border-acid transition-colors"
                          style={{ borderLeft: `2px solid ${t.primary_color}` }}>
                      {t.logo_url
                        ? <img src={t.logo_url} alt="" className="h-4 w-4 rounded-[2px] object-cover" />
                        : <Icon className="h-3.5 w-3.5" style={{ color: t.primary_color }} />}
                      <span className="text-xs">{t.name}</span>
                      {t.role === 'captain' && <Icons.Crown className="h-3 w-3 text-gold" />}
                    </Link>
                  )
                })}
              </div>
            )}
        </Tegel>

        <Tegel icon={MapPin} kleur="#7BC8FF" label="Mijn plek">
          {data.seat ? (
            <Link to="/seats"
                  className="flex items-center gap-2 hover:text-acid transition-colors">
              <span className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-border shrink-0
                               font-mono text-sm font-bold tabular-nums"
                    style={{ background: '#7BC8FF22', borderColor: '#7BC8FF55', color: '#7BC8FF' }}
                    title={`Tafel ${data.seat.table_name}`}>
                {String(data.seat.seat_number).slice(0, 3)}
              </span>
              <span className="text-sm">
                Tafel <strong>{data.seat.table_name}</strong>
                <span className="text-xs text-muted-foreground ml-1">plaats {data.seat.seat_number}</span>
              </span>
            </Link>
          ) : (
            <span className="text-muted-foreground">Zetel wordt door de organisatie toegewezen.</span>
          )}
        </Tegel>

        {/* NB: notification-stream is verhuisd naar NotificationBell in de header
            om dubbel-signaal te vermijden. Hier alleen structuurblokken. */}
        {data.upcoming_programme.length > 0 && (
          <Tegel icon={Calendar} kleur="#FFB57A" label="Programma volgend"
                 klas="lg:col-span-3">
            <div className="space-y-1">
              {data.upcoming_programme.slice(0, 3).map(p => (
                <div key={p.id} className="flex items-center gap-2 text-xs">
                  <span className="font-mono text-[10px] text-muted-foreground shrink-0 w-14">
                    {new Date(p.starts_at).toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span className="font-mono text-[9px] uppercase tracking-widest shrink-0"
                        style={{ color: p.track === 'zaal'    ? '#B4FF39'
                                        : p.track === 'bar'     ? '#FFB57A'
                                        : p.track === 'compo'   ? '#C77BFF'
                                        : p.track === 'kritiek' ? '#FF5A5A'
                                        :                         '#B4FF39' }}>
                    {p.track}
                  </span>
                  <span className="truncate flex-1">{p.title}</span>
                </div>
              ))}
            </div>
          </Tegel>
        )}
      </div>
    </section>
  )
}

function Tegel({ icon: Icon, kleur, label, telling, children, klas = '' }) {
  return (
    <div className={`rounded-md border border-border bg-card p-3 ${klas}`}>
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className="h-3.5 w-3.5" style={{ color: kleur }} />
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
        {telling != null && (
          <span className="font-mono text-[10px] tabular-nums ml-auto" style={{ color: kleur }}>{telling}</span>
        )}
      </div>
      <div className="text-xs">{children}</div>
    </div>
  )
}

function StatusChip({ status }) {
  const map = {
    registration: { label: 'inschrijving', kleur: '#FFB57A' },
    in_progress:  { label: 'live', kleur: '#B4FF39' },
    completed:    { label: 'klaar', kleur: '#94A3B8' },
  }
  const m = map[status] || { label: status, kleur: '#94A3B8' }
  return <span className="font-mono text-[9px] uppercase tracking-widest shrink-0" style={{ color: m.kleur }}>{m.label}</span>
}

function relatief(iso) {
  const s = Math.floor((Date.now() - new Date(iso)) / 1000)
  if (s < 60) return 'nu'
  if (s < 3600) return `${Math.floor(s / 60)}m`
  if (s < 86400) return `${Math.floor(s / 3600)}u`
  return `${Math.floor(s / 86400)}d`
}
